// Capital One connection card (Receipts Approval)
//
// Shows the state of the Plaid connection to Capital One and, only when it
// needs attention, a Reconnect Capital One button that repairs the existing
// connection (Plaid "update mode"). Needed after the primary card is replaced
// or when Capital One's consent for the connection is about to expire.
//
// Shown only to credit_card_manager and exec_admin. spikeAPIRouterService
// enforces the same groups, so hiding the card is convenience, not security.
//
// Usage (Wrapper is the page's own section component, so the card matches it):
//   <ReconnectCapitalOneCard Wrapper={ModuleBox} />
//
// Flow: status check -> (if needed) click -> createUpdateLinkToken -> Plaid
// Link opens -> Capital One sign-in (a pop-up window on desktop) -> onSuccess
// -> reconnectCompleted (logging only) -> status re-checked. Update mode keeps
// the same access token, so the hourly sync resumes on its own.

import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, HStack, Text, useToast } from '@chakra-ui/react';
import { usePlaidLink } from 'react-plaid-link';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AppContext } from '../AppContext.jsx';

const ALLOWED_GROUPS = ['credit_card_manager', 'exec_admin'];
const ENDPOINT = `${import.meta.env.VITE_API_BASE_URL}/create_link_token`;
const CONSENT_WARNING_DAYS = 30;   // matches plaidAuthServer

// POST to the router, then unwrap the Lambda proxy response it passes through
// ({ statusCode, body }), the same way qboClient does.
async function callReconnect(body) {
  const session = await fetchAuthSession();
  const idToken = session?.tokens?.idToken?.toString();
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify(body),
  });

  let parsed = null;
  try { parsed = await res.json(); } catch { parsed = null; }

  let status = res.status;
  if (parsed && typeof parsed.statusCode === 'number' && parsed.body !== undefined) {
    status = parsed.statusCode;
    try {
      parsed = typeof parsed.body === 'string' ? JSON.parse(parsed.body) : parsed.body;
    } catch { parsed = null; }
  }

  if (status >= 400) {
    const err = new Error((parsed && parsed.error) || `HTTP_${status}`);
    err.httpStatus = status;
    throw err;
  }
  return parsed;
}

function describeError(err) {
  if (err?.httpStatus === 403) return 'Your account does not have permission to reconnect Capital One.';
  if (err?.message === 'NO_EXISTING_CONNECTION') return 'There is no existing Capital One connection to repair. This needs technical support.';
  return 'The reconnect could not be started. Please try again in a few minutes.';
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// What the card says for each status. showButton decides whether the
// Reconnect button appears; tone sets the colour of the heading.
function describeStatus(s) {
  const days = typeof s?.daysRemaining === 'number' ? s.daysRemaining : null;
  switch (s?.status) {
    case 'working':
      return {
        tone: 'green.300',
        heading: 'Capital One connection: working',
        detail: days !== null
          ? `Capital One's approval for this connection lasts another ${plural(days, 'day')}. This card will ask for a reconnect ${CONSENT_WARNING_DAYS} days before it ends. No action needed.`
          : 'No action needed.',
        showButton: false,
      };
    case 'expiring':
      return {
        tone: 'orange.300',
        heading: days !== null && days >= 0
          ? `Capital One connection expires in ${plural(days, 'day')}`
          : 'Capital One connection is about to expire',
        detail: 'Reconnect now to avoid an interruption to new card transactions. Sign in with the Capital One login for the primary card. Use a computer, and allow the pop-up window if your browser asks. If Plaid asks which account to share, choose only the newest card.',
        showButton: true,
      };
    case 'needs_reconnect':
      return {
        tone: 'red.300',
        heading: 'Capital One connection needs to be reconnected',
        detail: 'New card transactions are not reaching the ERP until this is done. Sign in with the Capital One login for the primary card. Use a computer, and allow the pop-up window if your browser asks. If Plaid asks which account to share, choose only the newest card.',
        showButton: true,
      };
    case 'needs_support':
      return {
        tone: 'red.300',
        heading: 'Capital One connection is missing',
        detail: 'Reconnecting cannot fix this. It needs technical support.',
        showButton: false,
      };
    default:
      return {
        tone: 'orange.300',
        heading: 'Could not check the Capital One connection',
        detail: 'If an alert says the connection needs to be reconnected, use the button. Sign in with the Capital One login for the primary card.',
        showButton: true,
      };
  }
}

export function ReconnectCapitalOneCard({ Wrapper }) {
  const { store } = useContext(AppContext);
  const userGroups = store?.userData?.groups || [];
  const allowed = ALLOWED_GROUPS.some(g => userGroups.includes(g));

  const toast = useToast();
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);   // null while loading

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await callReconnect({ action: 'connectionStatus' }));
    } catch {
      setStatus({ status: 'unknown' });
    }
  }, []);

  useEffect(() => {
    if (allowed) loadStatus();
  }, [allowed, loadStatus]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (_publicToken, metadata) => {
      // The public token is not exchanged: update mode keeps the existing
      // connection. Only the account masks and types are sent, for the log.
      try {
        await callReconnect({
          action: 'reconnectCompleted',
          accounts: (metadata?.accounts || []).map(a => ({ mask: a.mask, subtype: a.subtype })),
        });
      } catch {
        // Logging only; the reconnect itself already succeeded.
      }
      setLinkToken(null);
      setBusy(false);
      toast({
        status: 'success',
        title: 'Capital One is reconnected',
        description: 'New card transactions will appear in the ERP within the hour. No further action needed.',
        duration: 12000,
        isClosable: true,
      });
      loadStatus();
    },
    onExit: (err) => {
      setLinkToken(null);
      setBusy(false);
      if (err) {
        toast({
          status: 'error',
          title: 'Capital One was not reconnected',
          description: 'The sign-in did not finish. Click Reconnect Capital One to try again.',
          duration: 10000,
          isClosable: true,
        });
      }
    },
  });

  // Open Plaid Link as soon as the token has loaded.
  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  async function startReconnect() {
    setBusy(true);
    try {
      const data = await callReconnect({ action: 'createUpdateLinkToken' });
      if (!data?.link_token) throw new Error('LINK_TOKEN_MISSING');
      setLinkToken(data.link_token);
    } catch (err) {
      setBusy(false);
      toast({
        status: 'error',
        title: 'Could not start the reconnect',
        description: describeError(err),
        duration: 10000,
        isClosable: true,
      });
    }
  }

  if (!allowed || status === null) return null;

  const view = describeStatus(status);
  const Outer = Wrapper || Box;

  // Healthy: one quiet line, no button.
  if (!view.showButton && status.status === 'working') {
    return (
      <Outer mb={4}>
        <Text fontSize="sm">
          <Text as="span" color={view.tone} fontWeight="bold">{view.heading}.</Text>{' '}
          {view.detail}
        </Text>
      </Outer>
    );
  }

  return (
    <Outer mb={4}>
      <HStack justify="space-between" align="center" flexWrap="wrap" rowGap={3}>
        <Box maxW="720px">
          <Text fontWeight="bold" color={view.tone}>{view.heading}</Text>
          <Text fontSize="sm">{view.detail}</Text>
        </Box>
        {view.showButton && (
          // type="button": this card sits inside the page's receipt form, and
          // a default submit button would submit that form.
          <Button type="button" colorScheme="orange" onClick={startReconnect} isLoading={busy} loadingText="Opening">
            Reconnect Capital One
          </Button>
        )}
      </HStack>
    </Outer>
  );
}

ReconnectCapitalOneCard.propTypes = {
  /** Section component to render inside, e.g. the page's ModuleBox. */
  Wrapper: PropTypes.elementType,
};

export default ReconnectCapitalOneCard;
